import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { CalendarDays, ChevronDown } from 'lucide-react';
import { NormalizedTicketStatus } from '@/utils/ticketStatus';
import { CheckOutline, TableAction } from '@/components';
import { getColumns, TICKET_STATUS_HSL } from './list';
import useLanguageStore from '@/stores/useLanguageStore';
import { TicketStatusEnum } from '@/stores';
import {
  useFetchPendingTicketCount,
  useFetchTicketFormFields,
  useFetchTickets,
  useFetchTicketsByIDs,
  useFilterTickets,
  useUpdateTicket,
} from '@/hooksApi';
import { SingleTicketResponse } from '@/types';
import { useQueryClient } from '@tanstack/react-query';
import { CustomMenuItemData_ActionTable } from '@/components/Action/ActionsTable';
import useTicketingStore, { TicketSearchByEnum, TicketSortKeyEnum } from '@/stores/useTicketingStore';
import TicketSearchPill from '@/components/TableAction/TicketSearchPill';
import { useNavigateWithPrevPageState } from '@/hooks/navigateWithPrevPageState';
import usePermission from '@/hooks/usePermission';
import { PermissionName } from '@/types';
import { useTicketsUrlSync } from '@/hooks/useTicketsUrlSync';
import { getStatusTranslationKeyFromRaw } from '@/utils/ticketStatus';

/* ─── Design tokens matching redesign ─── */
const C = {
  primaryBg: 'hsl(55,82%,61%)',       // yellow
  primaryFg: 'hsl(240,3%,23%)',        // dark text on yellow
  cardBg: '#ffffff',
  border: 'hsl(40,10%,90%)',           // light gray border
  mutedFg: 'hsl(220,10%,50%)',         // muted text
  mutedBg: 'hsl(40,10%,94%)',          // muted background
  fg: 'hsl(240,3%,23%)',               // foreground text
} as const;

/* ─── Status tab definitions matching redesign ─── */
const STATUS_TABS = [
  { key: 'all', labelKey: 'all', normalized: null },
  { key: 'new', labelKey: 'new', normalized: NormalizedTicketStatus.NEW },
  { key: 'pending', labelKey: 'awaiting_your_reply', normalized: NormalizedTicketStatus.AWAITING_REPLY },
  { key: 'on-hold', labelKey: 'on_hold', normalized: NormalizedTicketStatus.ON_HOLD },
  { key: 'solved', labelKey: 'solved', normalized: NormalizedTicketStatus.SOLVED },
] as const;

const pageSize = 15;

const TicketCenter = () => {
  const { t } = useTranslation();
  const [isClosingTicket, setIsClosingTicket] = useState(false);
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<string>('all');

  // Set tab from navigation state (e.g., dashboard Waiting Tickets banner)
  useEffect(() => {
    const initialTab = (location.state as any)?.initialTab;
    if (initialTab && STATUS_TABS.some(t => t.key === initialTab)) {
      const tab = STATUS_TABS.find(t => t.key === initialTab);
      if (tab) {
        setActiveTab(tab.key);
        if (tab.normalized) {
          setStatus([tab.normalized]);
          // Trigger search on next tick after status is set
          setTimeout(() => handleSearch(), 0);
        }
      }
    }
  }, [location.state]);
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const dateBtnRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();
  const { language } = useLanguageStore();
  const { hasPermission } = usePermission();
  const navigateWithPrevPageState = useNavigateWithPrevPageState();
  const { setStatus, searchText, searchBy, setSearchData } = useTicketingStore();

  const {
    currentPage,
    ticketFilters,
    sortKey,
    handleSearch,
    handleResetFilters,
    handlePaginationChange,
  } = useTicketsUrlSync();

  const { mutate: updateTicket } = useUpdateTicket();
  const { data: formData } = useFetchTicketFormFields(
    language,
    hasPermission(PermissionName.DASHBOARD),
  );

  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  // Per-status ticket counts for the tab badges. Backend returns the
  // upstream-aggregated `by_status` map plus a total, so we can map directly
  // onto each tab without re-counting on the client. Date filters propagate
  // so the badges stay consistent with the visible list.
  const { data: ticketCountsResp } = useFetchPendingTicketCount(
    hasPermission(PermissionName.DASHBOARD),
    {
      from_date: dateFrom || undefined,
      to_date: dateTo || undefined,
    },
  );
  const byStatus = ticketCountsResp?.object.by_status ?? {};
  const totalTickets = ticketCountsResp?.object.total ?? 0;
  // Tab keys map to upstream status slugs via the same normalization used by
  // backendToNormalizedStatus in src/utils/ticketStatus.ts. Keep them aligned
  // — if the normalization map changes, the badges must follow or they'll
  // disagree with the visible list contents.
  const getTabCount = (tabKey: string): number => {
    switch (tabKey) {
      case 'all':
        return totalTickets;
      case 'new':
        return (byStatus.open ?? 0) + (byStatus.in_progress ?? 0) + (byStatus.new ?? 0);
      case 'pending':
        return (byStatus.waiting ?? 0) + (byStatus.pending ?? 0);
      case 'on-hold':
        return (byStatus['on-hold'] ?? 0) + (byStatus.on_hold ?? 0);
      case 'solved':
        return (byStatus.resolved ?? 0) + (byStatus.closed ?? 0) + (byStatus.solved ?? 0);
      default:
        return 0;
    }
  };


  // fetch data
  const { data: ticketList, isFetching: isFetchingTicketList } = useFetchTickets(
    language,
    currentPage,
    pageSize,
    sortKey,
    !ticketFilters && !(searchBy || searchText) && sortKey === TicketSortKeyEnum.CREATED_DESC,
  );

  const { data: filteredTicketList, isFetching: isFilteringTicketList } = useFilterTickets(
    language,
    currentPage,
    pageSize,
    ticketFilters,
    searchBy === TicketSearchByEnum.PLATFORM_CODE ? searchText : undefined,
    sortKey,
    searchBy === TicketSearchByEnum.CUSTOM_ID ? searchText : undefined,
  );

  const searchedTicketId = searchBy === TicketSearchByEnum.TICKET_ID ? searchText : undefined;

  const { data: ticketListByIDs, isFetching: isFetchingTicketListByIDs } = useFetchTicketsByIDs(
    language,
    searchedTicketId ? [searchedTicketId] : undefined,
  );

  const getTicketMenuItems = useCallback(
    ({ id, status }: SingleTicketResponse): CustomMenuItemData_ActionTable[] => {
      return [
        {
          key: 'solve_ticket',
          label: t('solve_ticket'),
          leadingIcon: <CheckOutline width={16} height={16} color='#939496' />,
          loading: isClosingTicket,
          disabled:
            isClosingTicket ||
            status === TicketStatusEnum.SOLVED ||
            !hasPermission(PermissionName.SOLVE_TICKET),
          onClick: async () => {
            if (hasPermission(PermissionName.SOLVE_TICKET)) {
              updateTicket(
                { status: TicketStatusEnum.SOLVED, id },
                {
                  onSuccess: () => {
                    message?.success(t('operation_completed_successfully'));
                    queryClient.invalidateQueries(['tickets']);
                    setIsClosingTicket(false);
                  },
                  onError: () => {
                    setIsClosingTicket(false);
                  },
                },
              );
            }
          },
        },
      ];
    },
    [formData, hasPermission],
  );

  const searchedData = searchBy ?? (ticketFilters ? TicketSearchByEnum.FILTERS : undefined);

  let ticketData;
  if (searchedData === TicketSearchByEnum.TICKET_ID && searchText) {
    ticketData = { tickets: ticketListByIDs?.object, total: 1 };
  } else if (
    ticketFilters ||
    (searchedData === TicketSearchByEnum.PLATFORM_CODE && searchText) ||
    (searchedData === TicketSearchByEnum.CUSTOM_ID && searchText) ||
    sortKey !== TicketSortKeyEnum.CREATED_DESC
  ) {
    ticketData = {
      tickets: filteredTicketList?.object.tickets,
      total: filteredTicketList?.object.total,
    };
  } else {
    ticketData = {
      tickets: ticketList?.object.tickets,
      total: ticketList?.object.total,
    };
  }

  const isLoading = isFetchingTicketList || isFetchingTicketListByIDs || isFilteringTicketList;

  const handleTabChange = (tab: typeof STATUS_TABS[number]) => {
    setActiveTab(tab.key);
    if (tab.key === 'all' || !tab.normalized) {
      setStatus([]);
      handleResetFilters();
    } else {
      setStatus([tab.normalized]);
      handleSearch();
    }
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dateBtnRef.current &&
        !dateBtnRef.current.contains(e.target as Node) &&
        !(e.target as HTMLElement).closest('[data-ticket-date-dropdown]')
      ) {
        setDateDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className='relative w-full pb-6'>
      {/* ─── Status Tabs + Filter Controls (single row matching redesign) ─── */}
      <div className='flex items-center gap-2 pt-4 pb-3 overflow-x-auto'>
        {/* Status chip tabs — left side */}
        {STATUS_TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          const count = getTabCount(tab.key);
          return (
            <button
              key={tab.key}
              type='button'
              onClick={() => handleTabChange(tab)}
              className='inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[13px] font-semibold whitespace-nowrap border border-solid transition-colors cursor-pointer shrink-0'
              style={{
                backgroundColor: isActive ? C.primaryBg : C.cardBg,
                borderColor: isActive ? C.primaryBg : C.border,
                color: isActive ? C.primaryFg : C.mutedFg,
              }}
            >
              {String(t(tab.labelKey))}
              {count > 0 && (
                <span
                  className='inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 rounded-full text-[11px] font-semibold'
                  style={{
                    backgroundColor: isActive ? 'rgba(0,0,0,0.12)' : C.mutedBg,
                    color: isActive ? C.primaryFg : C.fg,
                  }}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}

        {/* Spacer pushes right-side controls */}
        <div className='flex-1' />

        {/* Search */}
        <TicketSearchPill
          searchText={searchText}
          searchBy={searchBy}
          onChange={(text, field) => setSearchData({ searchText: text, searchBy: field })}
          onClear={() => {
            setSearchData({ searchText: undefined, searchBy: undefined });
            handleResetFilters();
          }}
        />

        {/* Date filter dropdown */}
        <button
          ref={dateBtnRef}
          type='button'
          onClick={() => setDateDropdownOpen(!dateDropdownOpen)}
          className='inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-solid text-[12px] font-semibold transition-colors shrink-0 cursor-pointer'
          style={{
            borderColor: dateFrom || dateTo ? C.primaryBg : C.border,
            backgroundColor: dateFrom || dateTo ? 'hsla(55,82%,61%,0.1)' : C.cardBg,
            color: C.mutedFg,
          }}
        >
          <CalendarDays className='w-3.5 h-3.5' />
          {String(t('date'))}
          {(dateFrom || dateTo) && ' · set'}
          <ChevronDown className='w-3.5 h-3.5 opacity-60' />
        </button>

      </div>

      {/* ─── Date Dropdown Portal ─── */}
      {dateDropdownOpen &&
        dateBtnRef.current &&
        createPortal(
          <>
            <div
              className='fixed inset-0 z-[9998]'
              onClick={() => setDateDropdownOpen(false)}
            />
            <div
              data-ticket-date-dropdown
              className='fixed z-[9999] bg-white rounded-2xl border border-solid p-5 w-[300px]'
              style={{
                borderColor: C.border,
                boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.10)',
                top: dateBtnRef.current.getBoundingClientRect().bottom + 6,
                right: Math.max(12, window.innerWidth - dateBtnRef.current.getBoundingClientRect().right),
              }}
            >
              <p
                className='text-[10px] font-bold uppercase tracking-wider px-1 pb-2'
                style={{ color: C.mutedFg }}
              >
                {String(t('date_range')).toUpperCase()}
              </p>
              <div className='space-y-3'>
                <div>
                  <label className='text-[10px] block mb-1' style={{ color: C.mutedFg }}>
                    {String(t('calendar_date.from'))}
                  </label>
                  <input
                    type='date'
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className='w-full h-9 px-3 rounded-lg border border-solid text-[13px] focus:outline-none bg-white box-border'
                    style={{ borderColor: C.border, color: C.fg, maxWidth: '100%' }}
                  />
                </div>
                <div>
                  <label className='text-[10px] block mb-1' style={{ color: C.mutedFg }}>
                    {String(t('calendar_date.to'))}
                  </label>
                  <input
                    type='date'
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className='w-full h-9 px-3 rounded-lg border border-solid text-[13px] focus:outline-none bg-white box-border'
                    style={{ borderColor: C.border, color: C.fg, maxWidth: '100%' }}
                  />
                </div>
                <div className='flex items-center justify-end gap-2 pt-1'>
                  {(dateFrom || dateTo) && (
                    <button
                      type='button'
                      onClick={() => {
                        setDateFrom('');
                        setDateTo('');
                      }}
                      className='text-[12px] text-red-500 font-medium hover:underline cursor-pointer bg-transparent border-0 p-0'
                    >
                      {String(t('clear_all'))}
                    </button>
                  )}
                  <button
                    type='button'
                    onClick={() => {
                      handleSearch();
                      setDateDropdownOpen(false);
                    }}
                    className='h-8 px-4 rounded-lg text-[12px] font-semibold cursor-pointer border-0 transition-colors'
                    style={{ backgroundColor: C.primaryBg, color: C.primaryFg }}
                  >
                    {String(t('apply', { defaultValue: 'Apply' }))}
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body,
        )}

      {/* ─── Table ─── */}
      <TableAction
        columns={getColumns(t, formData?.object ?? [])}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        data={!isLoading ? (ticketData.tickets ?? []) : []}
        pageSize={pageSize}
        current={currentPage}
        total={ticketData.total}
        loading={isLoading}
        onPaginationChange={handlePaginationChange}
        rowSelectionEnabled={false}
        onRow={(record: any) => ({
          onClick: () => navigateWithPrevPageState(`/ticket-center/details/${record.id}`),
          className: 'cursor-pointer',
        })}
      />
    </div>
  );
};

export default TicketCenter;
